import React, { useEffect, useMemo, useState } from 'react'
import gql from 'graphql-tag'
import { useQuery } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import moment from 'moment-timezone'
import { Input } from 'reactstrap'
import './HistoryList.css'

const BQHistoryQuery = gql`
    query BQHistoryQuery($objectIds: [ID!]!, $startDate: Date, $endDate: Date) {
        bqHistory(objectIds: $objectIds, startDate: $startDate, endDate: $endDate) {
            items {
                appLabel
                modelName
                objectId
                eventTimestamp
                actorName
                isNew
                isDeleted
                snapshot
                dirtyFields
                comment
            }
        }
    }
`

const formatJson = (value) => {
    if (value === null || value === undefined) return '--'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const HIDDEN_CHANGE_FIELDS = new Set(['date_modified'])
const SNAPSHOT_FIELD_ALIASES = {
    modified_by: 'modified_by_id',
    created_by: 'created_by_id',
    assigned_by: 'assigned_by_id',
}

// Field changes render the raw field name (snake_case) so the trail reads like
// an audit log; null/empty values are shown as NULL.
const formatChangeValue = (value) => {
    if (value === null || value === undefined || value === '') return 'NULL'
    if (typeof value === 'string') return value

    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

// Humanize a field/model name: drop a trailing _id, split on _, Title Case.
const humanizeName = (name) => String(name || '')
    .replace(/_id$/, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const joinReadable = (labels) => {
    if (labels.length === 0) return ''
    if (labels.length === 1) return labels[0]
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

// When the backend has no comment for a row, summarize the event/changes so the
// timeline still shows a human-readable description of what happened.
const generateSummary = (item, changedFieldNames) => {
    const modelLabel = humanizeName(item.modelName) || 'record'
    if (item.isNew) return `New ${modelLabel.toLowerCase()} created.`
    if (item.isDeleted) return `${modelLabel} deleted.`
    if (!changedFieldNames.length) return 'Updated with no tracked field changes.'
    return `Updated ${joinReadable(changedFieldNames.map(humanizeName))}.`
}

const normalizeForComparison = (value) => {
    if (value === null || value === undefined || value === '') return null
    if (Array.isArray(value)) return value.map(normalizeForComparison)
    if (value && typeof value === 'object') {
        return Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                acc[key] = normalizeForComparison(value[key])
                return acc
            }, {})
    }
    return value
}

const areValuesEqual = (left, right) => (
    JSON.stringify(normalizeForComparison(left)) === JSON.stringify(normalizeForComparison(right))
)

const getSnapshotFieldName = (fieldName) => SNAPSHOT_FIELD_ALIASES[fieldName] || fieldName
const TODAY = moment().format(moment.HTML5_FMT.DATE)
const DEFAULT_END_DATE = TODAY
const DEFAULT_START_DATE = moment().subtract(6, 'days').format(moment.HTML5_FMT.DATE)
const MAX_HISTORY_RANGE_DAYS = 90

// Group newest-first items into day buckets, preserving order.
const groupItemsByDay = (items) => {
    const order = []
    const buckets = {}
    items.forEach((item) => {
        const key = item.eventTimestamp ? moment(item.eventTimestamp).format('YYYY-MM-DD') : 'unknown'
        if (!buckets[key]) {
            buckets[key] = []
            order.push(key)
        }
        buckets[key].push(item)
    })
    return order.map((key) => ({ dayKey: key, items: buckets[key] }))
}

const dayLabel = (dayKey) => {
    if (dayKey === 'unknown') return 'UNKNOWN DATE'
    const day = moment(dayKey, 'YYYY-MM-DD')
    const today = moment().startOf('day')
    let label
    if (day.isSame(today, 'day')) {
        label = 'TODAY'
    } else if (day.isSame(moment(today).subtract(1, 'day'), 'day')) {
        label = 'YESTERDAY'
    } else {
        label = day.format('dddd').toUpperCase()
    }
    return `${label} • ${day.format('MMM D, YYYY').toUpperCase()}`
}

function BQHistoryList({ objectIds = [], emptyText = 'No history found yet.', subject = null }) {
    const [visibleDetails, setVisibleDetails] = useState({})
    const [startDate, setStartDate] = useState(DEFAULT_START_DATE)
    const [endDate, setEndDate] = useState(DEFAULT_END_DATE)
    const [appliedStartDate, setAppliedStartDate] = useState(DEFAULT_START_DATE)
    const [appliedEndDate, setAppliedEndDate] = useState(DEFAULT_END_DATE)
    const [showDatePicker, setShowDatePicker] = useState(false)
    const normalizedObjectIds = useMemo(
        () => (objectIds || []).filter(Boolean),
        [objectIds]
    )
    const isDefaultDateRange = appliedStartDate === DEFAULT_START_DATE && appliedEndDate === DEFAULT_END_DATE
    const hasMissingDate = !startDate || !endDate
    const hasInvalidDateRange = !!(startDate && endDate && startDate > endDate)
    const hasRangeTooLarge = !!(
        startDate
        && endDate
        && moment(endDate).diff(moment(startDate), 'days') > (MAX_HISTORY_RANGE_DAYS - 1)
    )

    useEffect(() => {
        setVisibleDetails({})
    }, [normalizedObjectIds.join('|'), appliedStartDate, appliedEndDate])

    const { data, loading, error } = useQuery(BQHistoryQuery, {
        variables: {
            objectIds: normalizedObjectIds,
            startDate: appliedStartDate || null,
            endDate: appliedEndDate || null,
        },
        skip: !normalizedObjectIds.length,
        fetchPolicy: 'cache-and-network',
    })

    const rangeLabel = `${moment(appliedStartDate).format('MMM D')} - ${moment(appliedEndDate).format('MMM D, YYYY')}`

    const validationMessages = (
        <>
            {hasMissingDate && (
                <p className="vgm-audit-notice">Please select both a start date and an end date.</p>
            )}
            {hasInvalidDateRange && (
                <p className="vgm-audit-notice">The start date must be on or before the end date.</p>
            )}
            {hasRangeTooLarge && (
                <p className="vgm-audit-notice">Please select a date range of {MAX_HISTORY_RANGE_DAYS} days or less.</p>
            )}
        </>
    )

    const header = (
        <div className="vgm-audit-header">
            <div className="vgm-audit-header-titles">
                <h2 className="vgm-audit-title">Audit Trail</h2>
                <p className="vgm-audit-subtitle">
                    Complete version history and system event tracking{subject ? ` for ${subject}` : ''}
                </p>
            </div>
            <div className="vgm-audit-daterange">
                <button
                    type="button"
                    className="vgm-audit-daterange-badge"
                    onClick={() => setShowDatePicker((prev) => !prev)}
                >
                    <i className="fa fa-calendar" aria-hidden="true" />
                    <span>{rangeLabel}</span>
                </button>
                {showDatePicker && (
                    <div className="vgm-audit-daterange-popover">
                        <div className="vgm-audit-filter-field">
                            <label className="vgm-audit-filter-label" htmlFor="bq-history-start-date">From</label>
                            <Input
                                id="bq-history-start-date"
                                type="date"
                                value={startDate}
                                min={endDate ? moment(endDate).subtract(MAX_HISTORY_RANGE_DAYS - 1, 'days').format(moment.HTML5_FMT.DATE) : undefined}
                                max={endDate || undefined}
                                disabled={loading}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>
                        <div className="vgm-audit-filter-field">
                            <label className="vgm-audit-filter-label" htmlFor="bq-history-end-date">To</label>
                            <Input
                                id="bq-history-end-date"
                                type="date"
                                value={endDate}
                                min={startDate || undefined}
                                max={startDate ? moment.min(moment(startDate).add(MAX_HISTORY_RANGE_DAYS - 1, 'days'), moment(TODAY)).format(moment.HTML5_FMT.DATE) : TODAY}
                                disabled={loading}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                        {validationMessages}
                        <button
                            type="button"
                            className="vgm-audit-apply"
                            onClick={() => {
                                setAppliedStartDate(startDate)
                                setAppliedEndDate(endDate)
                                setShowDatePicker(false)
                            }}
                            disabled={hasMissingDate || hasInvalidDateRange || hasRangeTooLarge || loading}
                        >
                            Apply
                        </button>
                    </div>
                )}
            </div>
        </div>
    )

    const historyItems = data?.bqHistory?.items || []
    const getItemKey = (item, index) => (
        `${item.appLabel}-${item.modelName}-${item.objectId}-${item.eventTimestamp || index}-${index}`
    )
    const toggleDetails = (key) => {
        setVisibleDetails((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    if (loading && !historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <Skeleton height={90} count={4} className="mb-2" />
            </div>
        )
    }

    if (error) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <div className="alert alert-danger mb-0">{error.message}</div>
            </div>
        )
    }

    if (!historyItems.length) {
        return (
            <div className="vgm-audit-trail">
                {header}
                <p className="vgm-audit-empty">
                    {isDefaultDateRange
                        ? 'No history found in the last 7 days. Try expanding the date range.'
                        : emptyText}
                </p>
            </div>
        )
    }

    const dayGroups = groupItemsByDay(historyItems)

    return (
        <div className="vgm-audit-trail">
            {header}
            <div className="vgm-audit-timeline">
                {dayGroups.map((group) => (
                    <div key={group.dayKey} className="vgm-audit-day">
                        <div className="vgm-audit-day-label">{dayLabel(group.dayKey)}</div>
                        <div className="vgm-audit-day-items">
                            {group.items.map((item, index) => {
                                const itemKey = getItemKey(item, index)
                                const isSystem = !item.actorName
                                const isEvent = item.isNew || item.isDeleted
                                const visibleChanges = !isEvent && item.dirtyFields && typeof item.dirtyFields === 'object'
                                    ? Object.entries(item.dirtyFields).filter(([fieldName, oldValue]) => {
                                        const snapshotFieldName = getSnapshotFieldName(fieldName)
                                        return (
                                            !HIDDEN_CHANGE_FIELDS.has(fieldName)
                                            && !areValuesEqual(oldValue, item.snapshot?.[snapshotFieldName])
                                        )
                                    })
                                    : []
                                const isDetailsVisible = visibleDetails[itemKey]
                                // Use the backend comment when present; otherwise generate a
                                // summary of the event/changes so every row has a description.
                                const description = item.comment
                                    || generateSummary(item, visibleChanges.map(([fieldName]) => fieldName))
                                return (
                                    <div key={itemKey} className="vgm-audit-item">
                                        <div className="vgm-audit-item-time">
                                            {item.eventTimestamp ? moment(item.eventTimestamp).format('hh:mm:ss A') : '--'}
                                        </div>
                                        <span className={`vgm-audit-item-dot${item.isNew ? ' is-created' : ''}${item.isDeleted ? ' is-deleted' : ''}`} aria-hidden="true" />
                                        <div className="vgm-audit-item-body">
                                            <div className="vgm-audit-item-head">
                                                <span className="vgm-audit-item-object">{item.modelName || '--'} #{item.objectId}</span>
                                                <span className="vgm-audit-item-by">by</span>
                                                <span className={`vgm-audit-item-actor${isSystem ? ' is-system' : ''}`}>
                                                    {item.actorName || 'System'}
                                                </span>
                                            </div>

                                            {isEvent ? (
                                                <div className="vgm-audit-item-event">
                                                    <span className={`vgm-audit-badge ${item.isNew ? 'is-created' : 'is-deleted'}`}>
                                                        {item.isNew ? 'Created' : 'Deleted'}
                                                    </span>
                                                    {description && <span className="vgm-audit-event-text">{description}</span>}
                                                </div>
                                            ) : (
                                                <>
                                                    {description && <div className="vgm-audit-item-desc">{description}</div>}
                                                    {visibleChanges.length > 0 && (
                                                        <div className="vgm-audit-changes">
                                                            {visibleChanges.map(([fieldName, oldValue]) => {
                                                                const snapshotFieldName = getSnapshotFieldName(fieldName)
                                                                return (
                                                                    <div key={fieldName} className="vgm-audit-change">
                                                                        <span className="vgm-audit-change-field">{fieldName}</span>
                                                                        <span className="vgm-audit-change-colon">:</span>
                                                                        <span className="vgm-audit-change-old">{formatChangeValue(oldValue)}</span>
                                                                        <i className="fa fa-arrow-right vgm-audit-change-arrow" aria-hidden="true" />
                                                                        <span className="vgm-audit-change-new">{formatChangeValue(item.snapshot?.[snapshotFieldName])}</span>
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            <button type="button" className="vgm-audit-details-toggle" onClick={() => toggleDetails(itemKey)}>
                                                {isDetailsVisible ? 'Hide snapshot' : 'View snapshot'}
                                            </button>
                                            {isDetailsVisible && (
                                                <pre className="vgm-audit-json mb-0">{formatJson(item.snapshot)}</pre>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default BQHistoryList
